import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(v || 0));

export const StripeRefund = ({ payment, onClose, onSubmitted }) => {
  const [state, setState] = useState(null);
  const [mode, setMode] = useState('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get(`/sales/payments/${payment.id}/stripe-refund`).then(({ data }) => setState(data)).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [payment.id]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const body = { reason: reason.trim(), confirmed: true, ...(mode === 'partial' ? { amount } : {}) };
      const { data } = await api.post(`/sales/payments/${payment.id}/stripe-refund`, body);
      toast.success(data.duplicate ? 'That refund was already submitted; nothing was sent twice.' : `Stripe refund ${data.status}`);
      await load();
      await onSubmitted();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remaining = Number(state?.remaining_amount || 0);
  const invalid = reason.trim().length < 5 || remaining <= 0 || state?.inflight
    || (mode === 'partial' && !(Number(amount) > 0 && Number(amount) <= remaining));

  return <section className="border border-red-200 bg-white rounded-sm p-6 space-y-4" role="dialog" aria-label="Stripe refund" data-testid="stripe-refund-dialog">
    <div className="flex justify-between gap-3">
      <h2 className="font-semibold text-ei-navy">Refund this Stripe payment ({state?.mode || payment.environment})</h2>
      <button onClick={onClose} className="text-sm underline text-ei-navy" data-testid="stripe-refund-close">Close</button>
    </div>
    {error && <p role="alert" className="text-sm text-red-800" data-testid="stripe-refund-error">{error}</p>}
    {!state && !error && <p className="text-sm">Reading the verified capture from Stripe…</p>}
    {state && <>
      <dl className="grid grid-cols-2 gap-1 text-sm">
        <dt>Captured</dt><dd className="text-right" data-testid="stripe-refund-captured">{money(state.captured_amount)}</dd>
        <dt>Already refunded</dt><dd className="text-right" data-testid="stripe-refund-refunded">{money(state.refunded_amount)}</dd>
        <dt className="font-bold">Refundable remaining</dt><dd className="font-bold text-right" data-testid="stripe-refund-remaining">{money(state.remaining_amount)}</dd>
      </dl>
      {state.inflight && <p className="text-sm text-amber-900" data-testid="stripe-refund-inflight">A refund is pending reconciliation; no new refund can be submitted.</p>}
      {remaining <= 0 && <p className="text-sm text-ei-graphite/70" data-testid="stripe-refund-complete">This payment is fully refunded.</p>}
      {state.refunds.length > 0 && <ul className="text-xs space-y-1 text-ei-graphite/70" data-testid="stripe-refund-history">
        {state.refunds.map((r) => <li key={r.id} data-testid={`stripe-refund-row-${r.id}`}>{money(r.amount)} · {r.status} · {r.reason}</li>)}
      </ul>}
      {remaining > 0 && <>
        <div className="flex gap-4 text-sm">
          {['full', 'partial'].map((option) => <label key={option} className="inline-flex items-center gap-2">
            <input type="radio" name="stripe-refund-mode" checked={mode === option} onChange={() => setMode(option)} data-testid={`stripe-refund-mode-${option}`} />
            {option === 'full' ? `Full remaining ${money(state.remaining_amount)}` : 'Partial amount'}
          </label>)}
        </div>
        {mode === 'partial' && <label className="block text-sm">Amount AUD (max {money(state.remaining_amount)})
          <input type="number" min="0.01" step="0.01" max={remaining} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 block border border-ei-mist p-2 rounded-sm" data-testid="stripe-refund-amount" />
        </label>}
        <label className="block text-sm">Reason note (recorded against the refund)
          <input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full border border-ei-mist p-3 rounded-sm" data-testid="stripe-refund-reason" />
        </label>
        <p className="text-xs text-ei-graphite/65">The refund is sent to Stripe with an idempotency key, so a retry can never refund twice. Accounting sync and stock are not changed automatically.</p>
        <button onClick={submit} disabled={busy || invalid} className="bg-red-800 text-white rounded-sm px-5 py-3 text-sm disabled:opacity-50" data-testid="stripe-refund-submit">
          Refund {mode === 'full' ? money(state.remaining_amount) : money(amount)}
        </button>
      </>}
    </>}
  </section>;
};
